import React, { useState, useEffect } from 'react';
import { Modal } from '../ui/Modal';
import { Input } from '../ui/Input';
import { PrimaryButton } from '../ui/PrimaryButton';
import { GhostButton } from '../ui/GhostButton';
import { usePIM } from '../../context/PIMContext';
import { useSortableData } from '../../hooks/useSortableData';
import { SortableHeader } from '../ui/SortableHeader';
import { BASE_UPLOAD_URL } from '../../config';

const AccessoryRow = ({ acc, isSelected, onToggle, onChargeChange, selectedChargeId }) => {
    const [charges, setCharges] = useState([]);
    const [isLoadingCharges, setIsLoadingCharges] = useState(false);

    useEffect(() => {
        handleFetchCharges();
    }, []);

    const handleFetchCharges = async () => {
        if (charges.length > 0) return;
        setIsLoadingCharges(true);
        try {
            const token = localStorage.getItem('token');
            const response = await fetch(`/api/charges?sku=${acc.sku}`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            if (response.ok) {
                const data = await response.json();
                setCharges(data);
            }
        } catch (error) {
            console.error("Error fetching charges:", error);
        } finally {
            setIsLoadingCharges(false);
        }
    };

    return (
        <tr className={`hover:bg-stone-800/50 transition-colors ${isSelected ? 'bg-blue-900/20' : ''}`} onClick={() => onToggle(acc.id)}>
            <td className="p-3">
                <input
                    type="checkbox"
                    checked={isSelected}
                    onChange={() => { }} // Handled by row click
                    className="accent-stone-500 w-4 h-4 cursor-pointer"
                />
            </td>
            <td className="p-2">
                {acc.image_url ? (
                    <img src={`${BASE_UPLOAD_URL}/${acc.image_url.replace(/^\/+/, '')}`} alt={acc.model_name} className="w-10 h-10 object-cover rounded" />
                ) : (
                    <div className="w-10 h-10 bg-stone-700 rounded flex items-center justify-center text-xs text-stone-500">No Img</div>
                )}
            </td>
            <td className="p-3 font-medium text-stone-200">{acc.model_name}</td>
            <td className="p-3 text-stone-400">{acc.sku}</td>
            <td className="p-3" onClick={(e) => e.stopPropagation()}>
                <select
                    className="bg-stone-900 border border-stone-700 text-stone-300 text-xs rounded p-1 w-full focus:outline-none focus:border-blue-500"
                    value={selectedChargeId || ''}
                    onChange={(e) => onChargeChange(acc.id, e.target.value)}
                    disabled={!isSelected}
                >
                    <option value="">Latest / Default</option>
                    {isLoadingCharges && <option disabled>Loading...</option>}
                    {charges.map(charge => (
                        <option key={charge.id} value={charge.id}>
                            {charge.batch_number} ({charge.status})
                        </option>
                    ))}
                </select>
            </td>
        </tr>
    );
};

const AccessorySelectionModal = ({ isOpen, onClose, onAdd }) => {
    const { potentialAccessories, fetchPotentialAccessories } = usePIM();
    const [searchTerm, setSearchTerm] = useState('');
    const [selectedItems, setSelectedItems] = useState({}); // { [accId]: chargeId }

    useEffect(() => {
        if (isOpen) {
            console.log("AccessorySelectionModal FORCE UPDATE - Checking GhostButton availability...");
            fetchPotentialAccessories();
            setSelectedItems({});
        }
    }, [isOpen]);

    const filteredAccessories = (potentialAccessories || []).filter(acc =>
        acc.model_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        acc.sku.toLowerCase().includes(searchTerm.toLowerCase())
    );

    const { items: sortedAccessories, requestSort, sortConfig } = useSortableData(filteredAccessories);

    const handleToggle = (accId) => {
        setSelectedItems(prev => {
            const newItems = { ...prev };
            if (newItems.hasOwnProperty(accId)) {
                delete newItems[accId];
            } else {
                newItems[accId] = ''; // Default no charge
            }
            return newItems;
        });
    };

    const handleChargeChange = (accId, chargeId) => {
        setSelectedItems(prev => ({
            ...prev,
            [accId]: chargeId
        }));
    };

    const handleAddSelected = () => {
        const itemsToAdd = Object.entries(selectedItems).map(([id, chargeId]) => ({
            id: parseInt(id),
            chargeId
        }));
        onAdd(itemsToAdd);
    };

    return (
        <Modal isOpen={isOpen} onClose={onClose} title="Add Accessory" maxWidth="max-w-4xl">
            <div className="space-y-4 flex flex-col h-[600px]">
                <Input
                    placeholder="Search accessories..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="bg-stone-800 border-stone-600"
                />

                <div className="flex-1 overflow-y-auto border border-stone-700 rounded-lg flex flex-col">
                    <table className="w-full text-left border-collapse">
                        <thead className="text-xs text-stone-500 uppercase bg-stone-950 sticky top-0 z-10">
                            <tr>
                                <th className="px-3 py-2 border-b border-stone-800 w-10">
                                    {/* Select All could go here */}
                                </th>
                                <th className="px-3 py-2 border-b border-stone-800 w-16">Image</th>
                                <SortableHeader label="Model Name" sortKey="model_name" currentSort={sortConfig} onSort={requestSort} />
                                <SortableHeader label="SKU" sortKey="sku" currentSort={sortConfig} onSort={requestSort} />
                                <th className="px-3 py-2 border-b border-stone-800 w-40">Charge</th>
                            </tr>
                        </thead>
                        <tbody className="text-sm divide-y divide-stone-800 bg-stone-900/50">
                            {sortedAccessories.length === 0 ? (
                                <tr>
                                    <td colSpan="5" className="text-stone-500 text-center py-4">No accessories found.</td>
                                </tr>
                            ) : (
                                sortedAccessories.map(acc => (
                                    <AccessoryRow
                                        key={acc.id}
                                        acc={acc}
                                        isSelected={selectedItems.hasOwnProperty(acc.id)}
                                        onToggle={handleToggle}
                                        onChargeChange={handleChargeChange}
                                        selectedChargeId={selectedItems[acc.id]}
                                    />
                                ))
                            )}
                        </tbody>
                    </table>
                </div>

                <div className="flex justify-end gap-2 pt-4 border-t border-stone-700">
                    <GhostButton onClick={onClose}>Cancel</GhostButton>
                    <PrimaryButton onClick={handleAddSelected} disabled={Object.keys(selectedItems).length === 0}>
                        Add Selected ({Object.keys(selectedItems).length})
                    </PrimaryButton>
                </div>
            </div>
        </Modal>
    );
};

export default AccessorySelectionModal;
